import { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import { GridPage, groupPlacements, usePacking } from "./gridView/GridViewBuilder"
import { LayoutPreview, TableMeasurer } from "./gridView/LayoutPreview"
import { PlacementList } from "./gridView/PlacementList"
import { Filmstrip } from "./gridView/Filmstrip"
import { optimizeImageSizes, fillEmptySpace } from "./gridView/optimizers"

// Fallback quando config.meta non specifica valori
const DEFAULT_LAST_PAGE_ROWS = 12   // nessun banner → pagina intera disponibile
const DEFAULT_COVER_START_ROW = 0   // nessuna cover con prodotti

/**
 * GridView — layout v3
 *
 * Layout 3 colonne:
 *   [Filmstrip verticale] | [Preview pagina singola snap] | [Lista CSV editabile]
 *
 * Navigazione:
 *   - Click filmstrip   → cambia pagina + scroll lista al PageHeader
 *   - Wheel su preview  → cambia pagina (snap, no scrollbar)
 *   - Click item preview → seleziona item (lista: auto-expand + scroll)
 *   - Click riga CSV    → seleziona item + cambia pagina (preview + filmstrip)
 *
 * Props:
 *   onLaunch  {function} — callback "Conferma modifiche"
 *   onExit    {function} — callback "Annulla e chiudi"
 */
/**
 * Parser CSV RFC 4180.
 * Gestisce: campi quotati, virgole nei valori, virgolette doppie, newline nei campi.
 * @param {string} text - contenuto del file CSV (già decodificato UTF-8)
 * @returns {object[]}  - array di oggetti { header: valore }
 */
function parseCsv(text) {
  // Tokenizza rispettando i campi quotati (può contenere \n, virgole, <br>, ecc.)
  function parseRow(str, pos) {
    const values = []
    while (pos <= str.length) {
      if (str[pos] === '"') {
        // Campo quotato
        let val = ''
        pos++ // salta " aperta
        while (pos < str.length) {
          if (str[pos] === '"' && str[pos + 1] === '"') { val += '"'; pos += 2 }
          else if (str[pos] === '"') { pos++; break }
          else val += str[pos++]
        }
        values.push(val)
        if (str[pos] === ',') pos++
        else break
      } else {
        // Campo non quotato
        const end = str.indexOf(',', pos)
        const eol = str.indexOf('\n', pos)
        const stop = end === -1 ? eol : eol === -1 ? end : Math.min(end, eol)
        if (stop === -1) { values.push(str.slice(pos).replace(/\r$/, '')); break }
        if (stop === eol) { values.push(str.slice(pos, stop).replace(/\r$/, '')); break }
        values.push(str.slice(pos, stop))
        pos = stop + 1
      }
    }
    return values
  }

  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  // Trova fine prima riga (header) saltando campi quotati con \n
  let pos = 0; let depth = 0
  while (pos < normalized.length) {
    if (normalized[pos] === '"') depth = 1 - depth
    if (normalized[pos] === '\n' && depth === 0) break
    pos++
  }
  const headers = parseRow(normalized, 0)
  const rows = []
  let cur = pos + 1
  while (cur < normalized.length) {
    let end = cur; depth = 0
    while (end < normalized.length) {
      if (normalized[end] === '"') depth = 1 - depth
      if (normalized[end] === '\n' && depth === 0) break
      end++
    }
    const line = normalized.slice(cur, end)
    if (line.trim()) {
      const values = parseRow(line, 0)
      const obj = {}
      headers.forEach((h, i) => { obj[h] = values[i] ?? '' })
      rows.push(obj)
    }
    cur = end + 1
  }
  return rows
}

/**
 * Ricostruisce le righe CSV dai placements aggiornati.
 * Le righe vengono emesse nell'ordine dei placements (nuovo ordine utente).
 * Ogni riga riceve: colonna `sort` = indice progressivo, `Layout` e `Image Size` aggiornati.
 * I testi eventualmente modificati (Article Name, Article Text) vengono letti da product/tableRows.
 */
function buildExportRows(placements, config) {
  const csvM = config?.meta?.csvMapping ?? {}
  const fLayout  = csvM['layout']  ?? 'Layout'
  const fImgSize = csvM['imgSize'] ?? 'Image Size'
  const rows = []
  let sortIdx = 0
  for (const p of placements) {
    // tableRows (T/T2) o products (bkg multi-slot): emetti tutte le sub-righe
    const subRows = p.tableRows ?? p.products
    if (subRows && subRows.length > 0) {
      const isBkg2 = p.layoutId === 'bkg2'
      for (const row of subRows) {
        // bkg2: ogni prodotto ha il proprio Image Size; bkg1 e altri usano p.imgSize
        const rowImgSize = (isBkg2 && row[fImgSize]) ? row[fImgSize] : p.imgSize
        rows.push({ ...row, sort: sortIdx, [fLayout]: p.layoutId, [fImgSize]: rowImgSize })
        sortIdx++
      }
    } else {
      rows.push({ ...p.product, sort: sortIdx, [fLayout]: p.layoutId, [fImgSize]: p.imgSize })
      sortIdx++
    }
  }
  return rows
}

function GridViewInner({ onLaunch, onExit, initialCsvData, layoutConfig, onReady, onInfo, hasCoverProducts = false }, ref) {
  const config = layoutConfig
  const isIntegrated = !!initialCsvData

  // Parametri config-driven con fallback generici
  const GRID_COLS      = config?.meta?.gridCols      ?? 6
  const GRID_ROWS      = config?.meta?.gridRows      ?? 12
  const PAGE_W_MM      = config?.meta?.pageWidthMm   ?? 175
  const PAGE_H_MM      = config?.meta?.pageHeightMm  ?? 250
  const PAGE_GAP_MM    = config?.meta?.gapMm         ?? 3.5
  const LAST_PAGE_ROWS = config?.meta?.lastPageRows  ?? DEFAULT_LAST_PAGE_ROWS
  const HAS_COVER             = config?.meta?.hasCover              ?? true
  const COVER_START_ROW       = config?.meta?.cover?.startRow       ?? DEFAULT_COVER_START_ROW
  const COVER_ALLOWED_LAYOUTS = config?.meta?.cover?.allowedLayouts ?? null

  const [placements, setPlacements] = useState([])
  const [fileName, setFileName] = useState(null)
  const [error, setError] = useState(null)
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [overflowSet, setOverflowSet] = useState(new Set())
  const [textMeasures, setTextMeasures] = useState(new Map())
  const [tierOverrides, setTierOverrides] = useState(new Map())
  const [preDetectBatch, setPreDetectBatch] = useState([])  // array di placement indicizzati da renderizzare
  const preDetecting = preDetectBatch.length > 0
  const showImages = true

  // ── Dual advisor system ────────────────────────────────────────────────────────
  const [imageFitSuggestions, setImageFitSuggestions] = useState(new Map())
  const [spaceFillSuggestions, setSpaceFillSuggestions] = useState(new Map())
  const [activeAdvisors, setActiveAdvisors] = useState(new Set()) // 'imageFit' | 'spaceFill' — visibility only
  const skipImageFitRef = useRef(false)
  const skipSpaceFillRef = useRef(false)

  // Cambio pagina: mostra spinner breve, poi renderizza la nuova pagina
  const [displayPage, setDisplayPage] = useState(1)
  const [pageLoading, setPageLoading] = useState(false)

  const listRef = useRef(null)
  const previewRef = useRef(null)
  const pageContainerRef = useRef(null)

  // onWheel deve essere { passive: false } per poter chiamare preventDefault().
  // React imposta i wheel listener come passivi di default, quindi usiamo useEffect.
  useEffect(() => {
    const el = previewRef.current
    if (!el) return
    const handler = e => {
      e.preventDefault()
      if (e.deltaY > 0) setCurrentPage(p => Math.min(p + 1, totalPages || 1))
      if (e.deltaY < 0) setCurrentPage(p => Math.max(p - 1, 1))
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  })  // ri-registra ad ogni render per leggere pages.length aggiornato

  // ── Reflow tabelle: split misurato per blocchi T/T2 troppo grandi ──────────

  // Placements raw non ancora misurati (needsMeasurement:true, non ancora processati).
  // Dopo la misurazione, il raw viene rimpiazzato in-place da N chunk placements
  // (o resta uno solo se entra tutto). Ognuno ha `measured:true` per evitare loop.
  const placementsNeedingMeasurement = placements.filter(p =>
    p.needsMeasurement && !p.measured
  )

  // Callback: quando TableMeasurer termina, salva le altezze sul placement.
  // Lo split effettivo (chunking) viene deciso dal packing a runtime in base
  // allo spazio disponibile sulla pagina — come fa InDesign con i frame linkati.
  const handleTableMeasured = (placement, { rowHeights, headerHeight }) => {
    setPlacements(prev => {
      const idx = prev.findIndex(p =>
        p.blockId === placement.blockId && p.needsMeasurement && !p.measured
      )
      if (idx === -1) return prev
      const updated = { ...prev[idx], rowHeights, headerHeight, measured: true }
      return [...prev.slice(0, idx), updated, ...prev.slice(idx + 1)]
    })
  }

  // ── Packing + mappe derivate ─────────────────────────────────────────────────

  const pages = usePacking(placements, config, GRID_COLS, GRID_ROWS, LAST_PAGE_ROWS, tierOverrides,
    hasCoverProducts ? COVER_START_ROW : 0)

  // allPages e pageOffset dipendono da HAS_COVER e hasCoverProducts:
  // - nessuna cover (HAS_COVER=false): pages diretti, prodotti da pagina 1
  // - cover con prodotti: pages diretti, pagina 1 = cover con prodotti in righe 9-12
  // - cover statica (default): [] preposto, prodotti da pagina 2
  const allPages = pages.length > 0
    ? (!HAS_COVER || hasCoverProducts ? pages : [[], ...pages])
    : []
  const totalPages = allPages.length
  const pageOffset = (!HAS_COVER || hasCoverProducts) ? 1 : 2
  const pageStartMap = {}
  pages.forEach((page, i) => {
    if (page.length > 0 && page[0].originalIndex != null) {
      pageStartMap[page[0].originalIndex] = i + pageOffset
    }
  })

  // indexToPage[originalIndex] = pageNum  (per tutti gli item)
  const indexToPage = {}
  pages.forEach((page, i) => {
    page.forEach(p => {
      if (p.originalIndex != null) indexToPage[p.originalIndex] = i + pageOffset
    })
  })

  // Segue l'elemento selezionato se cambia pagina dopo un re-packing
  // (es. cambio layout → elemento si sposta dalla pagina 3 alla 2).
  // Attivo solo per modifiche locali al prodotto selezionato.
  const followSelectionRef = useRef(false)
  useEffect(() => {
    if (!followSelectionRef.current) return
    followSelectionRef.current = false
    if (selectedIndex === null) return
    const newPage = indexToPage[selectedIndex]
    if (newPage != null && newPage !== currentPage) {
      setCurrentPage(newPage)
    }
  }, [pages]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cambio pagina: spinner → swap → render
  useEffect(() => {
    if (currentPage === displayPage) return
    setPageLoading(true)
    const t = requestAnimationFrame(() => {
      setDisplayPage(currentPage)
      setPageLoading(false)
    })
    return () => cancelAnimationFrame(t)
  }, [currentPage])

  // Modalità integrata: carica initialCsvData automaticamente
  useEffect(() => {
    if (initialCsvData && placements.length === 0) {
      try {
        const rows = parseCsv(initialCsvData)
        setPlacements(groupPlacements(rows, config))
        setFileName('gridview-data.csv')
        setCurrentPage(1)
        setSelectedIndex(null)
      } catch (err) {
        setError(err.message)
      }
    }
  }, [initialCsvData]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pre-detection overflow: rileva overflow di tutti i prodotti in una passata ─

  const PRE_DETECT_BATCH_SIZE = 40

  useEffect(() => {
    if (placements.length > 0 && overflowSet.size === 0 && !preDetecting) {
      // Aspetta che i font siano pronti prima di avviare la pre-detection,
      // altrimenti il browser usa font fallback più larghi e genera falsi overflow
      document.fonts.ready.then(() => {
        const first = placements.slice(0, PRE_DETECT_BATCH_SIZE).map((p, i) => ({ ...p, _idx: i }))
        setPreDetectBatch(first)
      })
    }
  }, [placements.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreDetectOverflow = useCallback((originalIndex, slotIdx) => {
    const key = slotIdx != null ? `${originalIndex}:${slotIdx}` : originalIndex
    setOverflowSet(prev => {
      if (prev.has(key)) return prev
      return new Set([...prev, key])
    })
  }, [])

  const handlePreDetectTextMeasure = useCallback((originalIndex, scrollHeight, slotIdx) => {
    const key = slotIdx != null ? `${originalIndex}:${slotIdx}` : originalIndex
    setTextMeasures(prev => {
      if (prev.get(key) === scrollHeight) return prev
      const next = new Map(prev)
      next.set(key, scrollHeight)
      return next
    })
  }, [])

  // Dopo che il batch corrente è montato e misurato, avanza al prossimo o termina
  useEffect(() => {
    if (!preDetecting) return
    document.fonts.ready.then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const lastIdx = preDetectBatch[preDetectBatch.length - 1]._idx
          const nextStart = lastIdx + 1
          if (nextStart < placements.length) {
            const next = placements.slice(nextStart, nextStart + PRE_DETECT_BATCH_SIZE)
              .map((p, i) => ({ ...p, _idx: nextStart + i }))
            setTimeout(() => setPreDetectBatch(next), 0)
          } else {
            setPreDetectBatch([])
          }
        })
      })
    })
  }, [preDetectBatch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Segnala al parent che il GridView è pronto (placements caricati, pagine calcolate e DOM dipinto)
  useEffect(() => {
    if (placements.length > 0 && totalPages > 0 && !preDetecting) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onReady?.()
        })
      })
    }
  }, [placements.length, totalPages, preDetecting]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const handleOverflow = useCallback((originalIndex, slotIdx) => {
    const key = slotIdx != null ? `${originalIndex}:${slotIdx}` : originalIndex
    setOverflowSet(prev => {
      if (prev.has(key)) return prev
      return new Set([...prev, key])
    })
  }, []) // setOverflowSet è stabile, nessuna dipendenza esterna

  // Tier dinamico: aggiorna il tier di un layout tabella quando la misurazione reale
  // indica un tier diverso da quello attuale. Evita loop: se il valore è uguale, restituisce
  // lo stesso riferimento Map → nessun re-render.
  const handleTierChange = useCallback((originalIndex, rows, cols) => {
    setTierOverrides(prev => {
      const current = prev.get(originalIndex)
      if (current?.rows === rows && current?.cols === cols) return prev
      const next = new Map(prev)
      next.set(originalIndex, { rows, cols })
      return next
    })
  }, [])

  function scrollListToPage(pageNum) {
    const container = listRef.current
    const el = container?.querySelector(`[data-list-page-anchor="${pageNum}"]`)
    if (!container || !el) return
    const columnHeader = container.querySelector('[data-list-column-header]')
    const headerH = columnHeader ? columnHeader.offsetHeight : 0
    const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - headerH
    container.scrollTo({ top, behavior: 'smooth' })
  }

  // ── File input ───────────────────────────────────────────────────────────────

  function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    setError(null)
    const reader = new FileReader()
    reader.onload = evt => {
      try {
        const rows = parseCsv(evt.target.result)
        setPlacements(groupPlacements(rows, config))
        setFileName(file.name)
        setCurrentPage(1)
        setSelectedIndex(null)
      } catch (err) {
        setError(err.message)
      }
    }
    reader.readAsText(file, 'UTF-8')
  }

  // ── Handlers modifica dati ───────────────────────────────────────────────────

  function handleReorder(newPlacements) {
    setPlacements(prev => {
      const indexMap = new Map()
      for (let newIdx = 0; newIdx < newPlacements.length; newIdx++) {
        const oldIdx = prev.indexOf(newPlacements[newIdx])
        if (oldIdx !== -1) indexMap.set(oldIdx, newIdx)
      }

      function remapKey(key) {
        if (typeof key === 'string' && key.includes(':')) {
          const [idx, slot] = key.split(':')
          const newIdx = indexMap.get(Number(idx))
          return newIdx != null ? `${newIdx}:${slot}` : null
        }
        const newIdx = indexMap.get(key)
        return newIdx != null ? newIdx : null
      }

      setOverflowSet(prevSet => {
        const next = new Set()
        for (const key of prevSet) {
          const mapped = remapKey(key)
          if (mapped != null) next.add(mapped)
        }
        return next
      })

      setTextMeasures(prevMap => {
        const next = new Map()
        for (const [key, val] of prevMap) {
          const mapped = remapKey(key)
          if (mapped != null) next.set(mapped, val)
        }
        return next
      })

      setTierOverrides(prevMap => {
        const next = new Map()
        for (const [key, val] of prevMap) {
          const mapped = indexMap.get(key)
          if (mapped != null) next.set(mapped, val)
        }
        return next
      })

      return newPlacements
    })
  }

  // Reset chirurgico: cancella overflow solo per l'indice modificato
  function clearOverflowFor(index, slotIdx) {
    setOverflowSet(prev => {
      const next = new Set(prev)
      let changed = false
      if (slotIdx != null) {
        const slotKey = `${index}:${slotIdx}`
        if (next.has(slotKey)) { next.delete(slotKey); changed = true }
      } else {
        if (next.has(index)) { next.delete(index); changed = true }
        const prefix = `${index}:`
        for (const key of prev) {
          if (typeof key === 'string' && key.startsWith(prefix)) { next.delete(key); changed = true }
        }
      }
      return changed ? next : prev
    })
  }

  function handleChange(index, field, value) {
    followSelectionRef.current = true
    setPlacements(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p))
    clearOverflowFor(index)
  }

  const csvM = config?.meta?.csvMapping ?? {}
  const fImgSize = csvM['imgSize'] ?? 'Image Size'
  const TEXT_FIELDS = [csvM['articleName'] ?? 'Article Name', csvM['articleDescription'] ?? 'Article Text']

  function handleChangeProduct(index, field, value) {
    followSelectionRef.current = true
    if (TEXT_FIELDS.includes(field)) skipImageFitRef.current = false
    setPlacements(prev => prev.map((p, i) => {
      if (i !== index) return p
      const updated = { ...p, product: { ...p.product, [field]: value } }
      if (p.tableRows && p.tableRows.length > 0) {
        updated.tableRows = [{ ...p.tableRows[0], [field]: value }, ...p.tableRows.slice(1)]
      } else if (p.products && p.products.length > 0) {
        updated.products = [{ ...p.products[0], [field]: value }, ...p.products.slice(1)]
      }
      return updated
    }))
    clearOverflowFor(index)
  }

  function handleChangeTableRow(placementIndex, rowIndex, field, value) {
    followSelectionRef.current = true
    if (TEXT_FIELDS.includes(field)) skipImageFitRef.current = false
    let isProducts = false
    setPlacements(prev => prev.map((p, i) => {
      if (i !== placementIndex) return p
      const key = p.tableRows ? 'tableRows' : p.products ? 'products' : null
      if (!key) return p
      if (key === 'products') isProducts = true
      const updated = p[key].map((row, j) =>
        j === rowIndex ? { ...row, [field]: value } : row
      )
      return { ...p, [key]: updated }
    }))
    clearOverflowFor(placementIndex, isProducts ? rowIndex : undefined)
  }

  // ── Dual advisors: both always compute independently (debounced 300ms) ──────
  const imageFitTimerRef = useRef(null)
  const spaceFillTimerRef = useRef(null)

  useEffect(() => {
    if (preDetecting) return
    if (skipImageFitRef.current) { skipImageFitRef.current = false; return }
    const opts = config?.meta?.optimizations?.imageSize
    if (!opts?.enabled) { setImageFitSuggestions(new Map()); return }
    clearTimeout(imageFitTimerRef.current)
    imageFitTimerRef.current = setTimeout(() => {
      const preDetectW = pageContainerRef.current?.getBoundingClientRect().width || 480
      const scale = preDetectW / PAGE_W_MM
      setImageFitSuggestions(optimizeImageSizes(placements, config, overflowSet, textMeasures, scale, indexToPage))
    }, 300)
    return () => clearTimeout(imageFitTimerRef.current)
  }, [placements, config, preDetecting, textMeasures, overflowSet]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (preDetecting) return
    if (skipSpaceFillRef.current) { skipSpaceFillRef.current = false; return }
    const opts = config?.meta?.optimizations?.fillGaps
    if (!opts?.enabled) { setSpaceFillSuggestions(new Map()); return }
    clearTimeout(spaceFillTimerRef.current)
    spaceFillTimerRef.current = setTimeout(() => {
      setSpaceFillSuggestions(fillEmptySpace(pages, placements, config, LAST_PAGE_ROWS,
        hasCoverProducts ? COVER_START_ROW : 0))
    }, 300)
    return () => clearTimeout(spaceFillTimerRef.current)
  }, [placements, config, pages, preDetecting]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Undo system (per-advisor) ────────────────────────────────────────────────
  // entry: { id, type: 'single'|'page'|'global', advisor: 'imageFit'|'spaceFill', placements, prevSuggestions, timer }
  const [undoEntries, setUndoEntries] = useState([])
  const undoIdRef = useRef(0)

  useEffect(() => {
    return () => undoEntries.forEach(e => clearTimeout(e.timer))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function pushUndo(type, advisor, prevPlacements, prevSuggestions, meta = {}) {
    const id = ++undoIdRef.current
    const timer = setTimeout(() => {
      setUndoEntries(prev => prev.filter(e => e.id !== id))
    }, 5000)
    setUndoEntries(prev => [...prev, { id, type, advisor, placements: prevPlacements, prevSuggestions, timer, ...meta }])
    return id
  }

  function executeUndo(id) {
    const entry = undoEntries.find(e => e.id === id)
    if (!entry) return
    clearTimeout(entry.timer)
    if (entry.advisor === 'imageFit') {
      skipImageFitRef.current = true
      setImageFitSuggestions(entry.prevSuggestions)
    } else {
      skipSpaceFillRef.current = true
      setSpaceFillSuggestions(entry.prevSuggestions)
    }
    setPlacements(entry.placements)
    setUndoEntries(prev => prev.filter(e => e.id !== id))
  }

  function dismissUndo(id) {
    const entry = undoEntries.find(e => e.id === id)
    if (!entry) return
    clearTimeout(entry.timer)
    setUndoEntries(prev => prev.filter(e => e.id !== id))
  }

  // Helper: restituisce [getter, setter, skipRef] per l'advisor specificato
  function advisorState(advisor) {
    if (advisor === 'imageFit') return [imageFitSuggestions, setImageFitSuggestions, skipImageFitRef]
    return [spaceFillSuggestions, setSpaceFillSuggestions, skipSpaceFillRef]
  }

  function applySuggestionUpdates(s) {
    const updates = {}
    if (s.field === 'imgSize') updates.imgSize = s.suggested
    if (s.field === 'layout') {
      updates.layoutId = s.suggested
      if (s.suggestedImgSize) updates.imgSize = s.suggestedImgSize
    }
    return updates
  }

  function parseSlotKey(key) {
    if (typeof key === 'string' && key.includes(':')) {
      const [pi, si] = key.split(':').map(Number)
      return { placementIndex: pi, slotIdx: si }
    }
    return { placementIndex: key, slotIdx: null }
  }

  // Accetta un suggerimento di uno specifico advisor
  function acceptSuggestion(advisor, index) {
    const [suggs, setSuggs, skipRef] = advisorState(advisor)
    const s = suggs.get(index)
    if (!s || s.status !== 'pending') return
    followSelectionRef.current = true

    pushUndo('single', advisor, [...placements], new Map(suggs), { index })
    skipRef.current = true

    const { placementIndex, slotIdx } = parseSlotKey(index)
    if (slotIdx != null) {
      setPlacements(prev => prev.map((p, i) => {
        if (i !== placementIndex) return p
        const updated = (p.products ?? []).map((prod, j) =>
          j === slotIdx ? { ...prod, [fImgSize]: s.suggested } : prod
        )
        return { ...p, products: updated }
      }))
    } else {
      setPlacements(prev => prev.map((p, i) => i !== placementIndex ? p : { ...p, ...applySuggestionUpdates(s) }))
    }
    clearOverflowFor(placementIndex, slotIdx)
    setSuggs(prev => { const next = new Map(prev); next.set(index, { ...s, status: 'accepted' }); return next })
  }

  function rejectSuggestion(advisor, index) {
    const [suggs, setSuggs] = advisorState(advisor)
    const s = suggs.get(index)
    if (!s || s.status !== 'pending') return
    setSuggs(prev => { const next = new Map(prev); next.set(index, { ...s, status: 'rejected' }); return next })
  }

  // Applica tutti i suggerimenti pendenti di una pagina per un advisor
  function applyAllPage(advisor, pageNum) {
    const [suggs, setSuggs, skipRef] = advisorState(advisor)
    const page = allPages[pageNum - 1]
    if (!page) return

    // Raccogli chiavi pendenti: numeri diretti + chiavi composte "idx:slot"
    const pageIndices = new Set(page.map(p => p.originalIndex))
    const pendingKeys = []
    for (const [key, s] of suggs) {
      if (s.status !== 'pending') continue
      const { placementIndex } = parseSlotKey(key)
      if (pageIndices.has(placementIndex)) pendingKeys.push(key)
    }
    if (pendingKeys.length === 0) return

    pushUndo('page', advisor, [...placements], new Map(suggs), { pageNum })
    skipRef.current = true

    // Raggruppa per placement: slot updates e direct updates
    const slotUpdates = new Map() // placementIndex → [{slotIdx, suggested}]
    const directUpdates = new Set()
    for (const key of pendingKeys) {
      const s = suggs.get(key)
      const { placementIndex, slotIdx } = parseSlotKey(key)
      if (slotIdx != null) {
        if (!slotUpdates.has(placementIndex)) slotUpdates.set(placementIndex, [])
        slotUpdates.get(placementIndex).push({ slotIdx, suggested: s.suggested })
      } else {
        directUpdates.add(placementIndex)
      }
    }

    setPlacements(prev => prev.map((p, i) => {
      if (directUpdates.has(i)) {
        const s = suggs.get(i)
        return s ? { ...p, ...applySuggestionUpdates(s) } : p
      }
      if (slotUpdates.has(i)) {
        const updates = slotUpdates.get(i)
        const products = (p.products ?? []).map((prod, j) => {
          const upd = updates.find(u => u.slotIdx === j)
          return upd ? { ...prod, [fImgSize]: upd.suggested } : prod
        })
        return { ...p, products }
      }
      return p
    }))

    setOverflowSet(prev => {
      const next = new Set(prev)
      pendingKeys.forEach(key => {
        const { placementIndex } = parseSlotKey(key)
        next.delete(key)
        next.delete(placementIndex)
      })
      return next.size !== prev.size ? next : prev
    })

    setSuggs(prev => {
      const next = new Map(prev)
      pendingKeys.forEach(key => { const s = next.get(key); if (s) next.set(key, { ...s, status: 'accepted' }) })
      return next
    })
  }

  // Applica tutti i suggerimenti di un advisor su tutte le pagine
  function applyAll(advisor) {
    const [suggs, setSuggs, skipRef] = advisorState(advisor)
    const pendingKeys = [...suggs.entries()].filter(([, s]) => s.status === 'pending').map(([key]) => key)
    if (pendingKeys.length === 0) return

    pushUndo('global', advisor, [...placements], new Map(suggs))
    skipRef.current = true

    const slotUpdates = new Map()
    const directUpdates = new Set()
    for (const key of pendingKeys) {
      const s = suggs.get(key)
      const { placementIndex, slotIdx } = parseSlotKey(key)
      if (slotIdx != null) {
        if (!slotUpdates.has(placementIndex)) slotUpdates.set(placementIndex, [])
        slotUpdates.get(placementIndex).push({ slotIdx, suggested: s.suggested })
      } else {
        directUpdates.add(placementIndex)
      }
    }

    setPlacements(prev => prev.map((p, i) => {
      if (directUpdates.has(i)) {
        const s = suggs.get(i)
        return s ? { ...p, ...applySuggestionUpdates(s) } : p
      }
      if (slotUpdates.has(i)) {
        const updates = slotUpdates.get(i)
        const products = (p.products ?? []).map((prod, j) => {
          const upd = updates.find(u => u.slotIdx === j)
          return upd ? { ...prod, [fImgSize]: upd.suggested } : prod
        })
        return { ...p, products }
      }
      return p
    }))

    setOverflowSet(prev => {
      const next = new Set(prev)
      pendingKeys.forEach(key => {
        const { placementIndex } = parseSlotKey(key)
        next.delete(key)
        next.delete(placementIndex)
      })
      return next.size !== prev.size ? next : prev
    })

    setSuggs(prev => {
      const next = new Map(prev)
      pendingKeys.forEach(key => { const s = next.get(key); if (s) next.set(key, { ...s, status: 'accepted' }) })
      return next
    })
  }

  // ── Expose API to parent (GridViewDialog) ────────────────────────────────────

  useImperativeHandle(ref, () => ({
    triggerLaunch: () => handleLaunch(),
    setActiveAdvisors,
    applyAll,
    applyAllPage,
    undoEntries,
    executeUndo,
    dismissUndo,
    acceptSuggestion,
    rejectSuggestion,
  }))

  // Comunica info al parent per AppBar e SuggestionBar
  useEffect(() => {
    const countByStatus = (map) => {
      const vals = [...map.values()]
      return {
        total: map.size,
        pending: vals.filter(s => s.status === 'pending').length,
        accepted: vals.filter(s => s.status === 'accepted').length,
        rejected: vals.filter(s => s.status === 'rejected').length,
      }
    }
    onInfo?.({
      fileName,
      articleCount: placements.length,
      totalPages,
      currentPage,
      activeAdvisors: [...activeAdvisors],
      imageFit: countByStatus(imageFitSuggestions),
      spaceFill: countByStatus(spaceFillSuggestions),
      undoEntries,
    })
  }, [fileName, placements.length, totalPages, currentPage, activeAdvisors, imageFitSuggestions, spaceFillSuggestions, undoEntries]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Export CSV ───────────────────────────────────────────────────────────────

  function handleLaunch() {
    if (!placements.length) return
    const exportRows = buildExportRows(placements, config)

    // Serializza in CSV: header dalla prima riga, valori con escaping standard
    const headers = Object.keys(exportRows[0])
    const escape = v => {
      const s = String(v ?? '')
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s
    }
    const csv = [
      headers.map(escape).join(','),
      ...exportRows.map(row => headers.map(h => escape(row[h] ?? '')).join(','))
    ].join('\n')

    if (isIntegrated) {
      // Modalità integrata: passa il CSV al parent (GridViewDialog)
      onLaunch?.(csv)
    } else {
      // Modalità standalone: download CSV locale
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
        ? fileName.replace(/\.csv$/i, '_gridview.csv')
        : 'export_gridview.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      onLaunch?.()
    }
  }

  // ── Navigazione ──────────────────────────────────────────────────────────────

  // Click item in preview → seleziona item; PlacementList si auto-espande e scrolla
  function handleItemClick(originalIndex) {
    setSelectedIndex(originalIndex)
  }

  // Click riga CSV → seleziona item + sincronizza pagina preview e filmstrip
  function handleRowClick(idx) {
    setSelectedIndex(idx)
    if (idx == null) return
    const pageNum = indexToPage[idx]
    if (pageNum != null) setCurrentPage(pageNum)
  }

  // Click filmstrip → cambia pagina + scrolla PageHeader nella lista
  function handleFilmstripClick(pageNum) {
    setSelectedIndex(null)
    setCurrentPage(pageNum)
    scrollListToPage(pageNum)
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  if (!config) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <AlertTitle>Configuration missing</AlertTitle>
          No layout configuration found for this project. Please upload a layout_config.json file.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: isIntegrated ? '100%' : '100vh',
      boxSizing: 'border-box',
      background: '#f5f5f5',
      overflow: 'hidden',   // nessuno scroll sulla pagina
    }}>

      {/* TableMeasurer: misura altezze reali dei blocchi tabella troppo grandi
          per il tier massimo, così da calcolare gli split point del reflow. */}
      {placementsNeedingMeasurement.map(p => (
        <TableMeasurer
          key={`${p.blockId}|${p.imgSize}|${p.layoutId}`}
          config={config}
          layoutId={p.layoutId}
          imgSize={p.imgSize}
          tableRows={p.tableRows}
          onMeasured={data => handleTableMeasured(p, data)}
        />
      ))}

      {/* Pre-detection: container off-screen per rilevare overflow di tutti i prodotti */}
      {preDetecting && (() => {
        const preDetectW = pageContainerRef.current?.getBoundingClientRect().width || 480;
        const scale = preDetectW / PAGE_W_MM;
        const colW = ((PAGE_W_MM - (GRID_COLS - 1) * PAGE_GAP_MM) / GRID_COLS) * scale;
        const gapW = PAGE_GAP_MM * scale;
        return (
          <div style={{ position: 'absolute', left: '-9999px', visibility: 'hidden', width: preDetectW }}>
            {preDetectBatch.map(p => {
              const layout = config?.layouts?.find(l => l.id === p.layoutId)
              if (!layout || layout.type === 'table') return null
              const colSpan = layout.cols || 1;
              const itemW = colW * colSpan + gapW * (colSpan - 1);
              const productArray = p.products ?? (p.product ? [p.product] : [])
              return (
                <LayoutPreview
                  key={p._idx}
                  config={config}
                  layoutId={p.layoutId}
                  imgSize={p.imgSize}
                  products={productArray}
                  tableRows={p.tableRows ?? []}
                  precomputedWidth={itemW}
                  debug={false}
                  onOverflow={(slotIdx) => handlePreDetectOverflow(p._idx, slotIdx)}
                  onTextMeasure={(sh, slotIdx) => handlePreDetectTextMeasure(p._idx, sh, slotIdx)}
                  showImages={false}
                />
              )
            })}
          </div>
        )
      })()}

      {/* Sub-header — only in standalone mode */}
      {!isIntegrated && (
        <Paper elevation={1} square sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          flexShrink: 0,
          borderBottom: '1px solid #e0e0e0',
        }}>
          <input type="file" accept=".csv" onChange={handleFile} style={{ fontSize: 13 }} />
          {fileName && (
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              {fileName} — {placements.length} articles
            </span>
          )}
          {error && (
            <span style={{ fontSize: 13, color: '#dc2626' }}>Errore: {error}</span>
          )}
          <Box sx={{ flex: 1 }} />
          {totalPages > 0 && (
            <span style={{ fontSize: 12, color: '#9e9e9e' }}>
              Page {currentPage} / {totalPages}
            </span>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={handleLaunch}
            disabled={!placements.length}
            sx={{ textTransform: 'none', minWidth: 80 }}
          >
            Launch
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onExit}
            sx={{ textTransform: 'none', minWidth: 80 }}
          >
            Exit
          </Button>
        </Paper>
      )}

      {/* 3-column body — padding esterno crea spazio tra la card e lo sfondo */}
      {placements.length > 0 && (
        <Box sx={{ flex: 1, overflow: 'hidden', p: 2, display: 'flex', flexDirection: 'column' }}>
          <Paper elevation={2} sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            borderRadius: 1,
          }}>

            {/* Colonna 1: Filmstrip */}
            <Filmstrip
              pages={allPages}
              currentPage={currentPage}
              onPageClick={handleFilmstripClick}
              config={config}
              hasCover={HAS_COVER}
              hasBanner={LAST_PAGE_ROWS < GRID_ROWS}
              gridCols={GRID_COLS}
              gridRows={GRID_ROWS}
              pageWidthMm={PAGE_W_MM}
              pageHeightMm={PAGE_H_MM}
              gapMm={PAGE_GAP_MM}
              overflowSet={overflowSet}
              activeAdvisors={activeAdvisors}
              imageFitSuggestions={imageFitSuggestions}
              spaceFillSuggestions={spaceFillSuggestions}
            />

            {/* Colonna 2: Preview pagina singola (snap wheel via useEffect) */}
            <Box
              ref={previewRef}
              sx={{
                flexShrink: 0,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                px: '3em',
                pt: '6px',
                pb: 0,
                background: '#eeeeee',
                borderRight: '1px solid #e0e0e0',
              }}
            >
              <Box ref={pageContainerRef} sx={{
                width: 'calc((100vh - 160px - 6px) * 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                position: 'relative',
              }}>
                <div style={{
                  fontSize: 11,
                  color: '#555555',
                  userSelect: 'none',
                  letterSpacing: '0.04em',
                }}>
                  Page {displayPage} / {totalPages}
                </div>
                {pageLoading && (
                  <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    background: 'rgba(238,238,238,0.7)',
                  }}>
                    <CircularProgress size={32} />
                  </Box>
                )}
                <GridPage
                  config={config}
                  page={allPages[displayPage - 1] ?? []}
                  gridCols={GRID_COLS}
                  gridRows={GRID_ROWS}
                  pageWidthMm={PAGE_W_MM}
                  pageHeightMm={PAGE_H_MM}
                  gapMm={PAGE_GAP_MM}
                  debug={true}
                  hoveredIndex={hoveredIndex}
                  selectedIndex={selectedIndex}
                  onItemClick={handleItemClick}
                  isCoverPage={HAS_COVER && displayPage === 1}
                  isFinalPage={displayPage === totalPages && LAST_PAGE_ROWS < GRID_ROWS}
                  onOverflow={handleOverflow}
                  onTextMeasure={handlePreDetectTextMeasure}
                  overflowSet={overflowSet}
                  onTierChange={handleTierChange}
                  showImages={showImages}
                  activeAdvisors={activeAdvisors}
                  imageFitSuggestions={imageFitSuggestions}
                  spaceFillSuggestions={spaceFillSuggestions}
                  onAcceptSuggestion={acceptSuggestion}
                  onRejectSuggestion={rejectSuggestion}
                />
              </Box>
            </Box>

            {/* Colonna 3: Lista CSV editabile */}
            <Box sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              p: 1.5,
              gap: 1,
            }}>
              <div style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>
                {placements.length} articles — drag to sort
              </div>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <PlacementList
                  placements={placements}
                  config={config}
                  onReorder={handleReorder}
                  onChange={handleChange}
                  onChangeProduct={handleChangeProduct}
                  onChangeTableRow={handleChangeTableRow}
                  onHover={setHoveredIndex}
                  pageStartMap={pageStartMap}
                  activePage={currentPage}
                  onPageClick={handleFilmstripClick}
                  scrollContainerRef={listRef}
                  selectedIndex={selectedIndex}
                  onRowClick={handleRowClick}
                  overflowSet={overflowSet}
                  activeAdvisors={activeAdvisors}
                  imageFitSuggestions={imageFitSuggestions}
                  spaceFillSuggestions={spaceFillSuggestions}
                  onAcceptSuggestion={acceptSuggestion}
                  onRejectSuggestion={rejectSuggestion}
                  onApplyAllPage={applyAllPage}
                  undoEntries={undoEntries}
                  onUndo={executeUndo}
                  onDismissUndo={dismissUndo}
                  hasCoverProducts={hasCoverProducts}
                  coverAllowedLayouts={COVER_ALLOWED_LAYOUTS}
                  indexToPage={indexToPage}
                />
              </Box>
            </Box>

          </Paper>
        </Box>
      )}
    </Box>
  )
}

export const GridView = forwardRef(GridViewInner)
